import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Download } from "lucide-react";
import { API } from "@/lib/apiBase";
import { formatSlots } from "@/lib/formatSlot";

const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dy}`;
};

const iso = (d) => d.toISOString().slice(0, 10);

// Convenience presets — quick spans admins ask for most often.
const PRESETS = [
  { id: "today", label: "Today", days: 0 },
  { id: "7", label: "Last 7 days", days: 6 },
  { id: "30", label: "Last 30 days", days: 29 },
  { id: "mtd", label: "Month to date", mtd: true },
];

const STATUS_PILL = {
  confirmed: "bg-[#8cc63f] text-black",
  awaiting_confirmation: "bg-yellow-400 text-black",
  flagged_for_review: "bg-orange-400 text-black",
  pending: "bg-[#a1a1aa] text-black",
  cancelled: "bg-red-500 text-white",
  rejected: "bg-red-500 text-white",
};

const StatCard = ({ label, value, sub, accent }) => (
  <div className="card-surface p-4">
    <p className="label-text mb-1">{label}</p>
    <p className={`text-3xl font-black ${accent ? "text-[#8cc63f]" : "text-white"}`}>
      {value}
    </p>
    {sub && <p className="text-[10px] text-[#a1a1aa] mt-1 font-normal">{sub}</p>}
  </div>
);

const money = (n) =>
  `₱${(Number(n) || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RangeReport = () => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return iso(d);
  });
  const [endDate, setEndDate] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async (s, e) => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/range-report?start_date=${s}&end_date=${e}`
      );
      setReport(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(startDate, endDate);
  }, [fetchReport, startDate, endDate]);

  const applyPreset = (p) => {
    const now = new Date();
    if (p.mtd) {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(iso(first));
      setEndDate(iso(now));
      return;
    }
    const start = new Date();
    start.setDate(start.getDate() - p.days);
    setStartDate(iso(start));
    setEndDate(iso(now));
  };

  // Build a CSV export of the per-day + per-court + booking list. All three
  // sections are concatenated with header rows so ops can open it in Excel.
  const exportCSV = () => {
    if (!report) return;
    const lines = [];
    lines.push(`Assenda Sales Report,${report.start_date} to ${report.end_date}`);
    lines.push("");
    lines.push("SUMMARY");
    Object.entries(report.totals).forEach(([k, v]) => {
      lines.push(`${k},${v}`);
    });
    lines.push("");
    lines.push("PER DAY");
    lines.push("date,bookings_total,confirmed,revenue_confirmed,slots_booked");
    report.per_day.forEach((d) => {
      lines.push(`${d.date},${d.bookings_total},${d.confirmed},${d.revenue_confirmed},${d.slots_booked}`);
    });
    lines.push("");
    lines.push("PER COURT");
    lines.push("court,bookings,slots_booked,revenue");
    report.per_court.forEach((c) => {
      lines.push(`${c.court_name},${c.bookings},${c.slots_booked},${c.revenue}`);
    });
    lines.push("");
    lines.push("BOOKINGS");
    lines.push("date,customer,court,slots,status,amount");
    report.bookings.forEach((b) => {
      const slots = (b.time_slots || []).join(" ");
      lines.push([
        b.booking_date, `"${b.customer_name}"`, `"${b.court_name}"`,
        `"${slots}"`, b.status, b.total_amount || 0,
      ].join(","));
    });
    lines.push("");
    lines.push("MANUAL BLOCKS WITH AMOUNT");
    lines.push("date,court,slot,note,amount");
    (report.manual_blocks || []).forEach((m) => {
      const note = (m.public_note || m.private_note || "").replace(/"/g, "'");
      lines.push([
        m.booking_date, `"${m.court_name}"`, `"${m.time_slot}"`,
        `"${note}"`, m.amount,
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `assenda-sales_${report.start_date}_to_${report.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = report?.totals;

  return (
    <div className="space-y-6" data-testid="range-report">
      {/* Controls */}
      <div className="card-surface p-4 flex flex-col sm:flex-row gap-3 sm:items-end sm:flex-wrap">
        <div className="flex flex-col sm:flex-row gap-3">
          <label className="flex flex-col gap-1">
            <span className="label-text">From</span>
            <input
              type="date"
              data-testid="range-start"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-[#0f0f0f] border border-white/15 rounded-sm px-3 py-2 text-sm text-white focus:border-[#8cc63f] focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="label-text">To</span>
            <input
              type="date"
              data-testid="range-end"
              value={endDate}
              min={startDate}
              max={todayISO()}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-[#0f0f0f] border border-white/15 rounded-sm px-3 py-2 text-sm text-white focus:border-[#8cc63f] focus:outline-none"
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`range-preset-${p.id}`}
              onClick={() => applyPreset(p)}
              className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded-sm border border-white/15 text-[#a1a1aa] hover:text-[#8cc63f] hover:border-[#8cc63f]/40"
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="sm:ml-auto flex gap-2">
          <button
            data-testid="range-refresh"
            onClick={() => fetchReport(startDate, endDate)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-sm border border-white/15 text-white/80 hover:border-[#8cc63f]/40 hover:text-[#8cc63f] disabled:opacity-40"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            data-testid="range-export"
            onClick={exportCSV}
            disabled={!report || loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-[#8cc63f] text-black rounded-sm hover:bg-[#9ed15c] disabled:opacity-40"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {loading && !report && (
        <div className="card-surface p-8 text-center text-[#a1a1aa]">Loading…</div>
      )}

      {report && totals && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard
              label="Confirmed Revenue"
              value={money(totals.revenue_confirmed)}
              sub={`${totals.confirmed} confirmed booking(s)`}
              accent
            />
            <StatCard
              label="Manual Blocks Revenue"
              value={money(totals.manual_block_revenue || 0)}
              sub={`${totals.manual_blocks_count || 0} block(s) with amount`}
            />
            <StatCard
              label="Total Revenue"
              value={money(totals.total_revenue || totals.revenue_confirmed)}
              sub="Bookings + manual blocks"
              accent
            />
            <StatCard
              label="Total Bookings"
              value={totals.bookings_total}
              sub={`${report.days} day(s) · ${totals.courts_count} court(s)`}
            />
            <StatCard
              label="Court Utilization"
              value={`${totals.utilization_pct}%`}
              sub={`${totals.slots_booked} / ${totals.slots_total} slots`}
            />
          </div>

          {/* Status breakdown */}
          <div className="card-surface p-4">
            <p className="label-text mb-3">Booking Status Breakdown</p>
            <div className="flex flex-wrap gap-2">
              {[
                "confirmed", "awaiting_confirmation", "flagged_for_review",
                "pending", "cancelled", "rejected",
              ].map((k) => (
                <div key={k} className="flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_PILL[k] || "bg-white/10"}`}>
                    {k.replace(/_/g, " ")}
                  </span>
                  <span className="font-bold">{totals[k] || 0}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Per-court breakdown */}
          <div className="card-surface p-4">
            <p className="label-text mb-3">Per-Court Breakdown</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="text-[10px] uppercase tracking-widest text-[#a1a1aa]">
                  <tr>
                    <th className="text-left py-2">Court</th>
                    <th className="text-right py-2">Bookings</th>
                    <th className="text-right py-2">Slots</th>
                    <th className="text-right py-2">Booking Rev</th>
                    <th className="text-right py-2">Blocks</th>
                    <th className="text-right py-2">Block Rev</th>
                  </tr>
                </thead>
                <tbody>
                  {report.per_court.map((c) => (
                    <tr key={c.court_id} className="border-t border-white/5">
                      <td className="py-2 font-semibold">{c.court_name}</td>
                      <td className="py-2 text-right">{c.bookings}</td>
                      <td className="py-2 text-right">{c.slots_booked}</td>
                      <td className="py-2 text-right font-bold">{money(c.revenue)}</td>
                      <td className="py-2 text-right">{c.manual_blocks || 0}</td>
                      <td className="py-2 text-right font-bold text-[#8cc63f]">
                        {money(c.manual_block_revenue || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-day list (compact) */}
          <div className="card-surface p-4">
            <p className="label-text mb-3">Daily Trend</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="text-[10px] uppercase tracking-widest text-[#a1a1aa]">
                  <tr>
                    <th className="text-left py-2">Date</th>
                    <th className="text-right py-2">Bookings</th>
                    <th className="text-right py-2">Confirmed</th>
                    <th className="text-right py-2">Slots</th>
                    <th className="text-right py-2">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {report.per_day.map((d) => (
                    <tr key={d.date} className="border-t border-white/5">
                      <td className="py-1.5 font-semibold">{d.date}</td>
                      <td className="py-1.5 text-right">{d.bookings_total}</td>
                      <td className="py-1.5 text-right">{d.confirmed}</td>
                      <td className="py-1.5 text-right">{d.slots_booked}</td>
                      <td className="py-1.5 text-right font-bold">{money(d.revenue_confirmed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bookings list */}
          <div className="card-surface p-4">
            <p className="label-text mb-3">Bookings in Range ({report.bookings.length})</p>
            {report.bookings.length === 0 ? (
              <p className="text-sm text-[#a1a1aa]">No bookings in this range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="text-[10px] uppercase tracking-widest text-[#a1a1aa]">
                    <tr>
                      <th className="text-left py-2">Date</th>
                      <th className="text-left py-2">Customer</th>
                      <th className="text-left py-2">Court</th>
                      <th className="text-left py-2">Slots</th>
                      <th className="text-left py-2">Status</th>
                      <th className="text-right py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.bookings.map((b) => (
                      <tr key={b.id} className="border-t border-white/5">
                        <td className="py-2">{b.booking_date}</td>
                        <td className="py-2 font-semibold">{b.customer_name}</td>
                        <td className="py-2">{b.court_name}</td>
                        <td className="py-2 text-[#a1a1aa]">{formatSlots(b.time_slots || [])}</td>
                        <td className="py-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_PILL[b.status] || "bg-white/10"}`}>
                            {(b.status || "pending").replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="py-2 text-right font-bold">{money(b.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Manual blocks with amount */}
          <div className="card-surface p-4">
            <p className="label-text mb-3">
              Manual Blocks With Amount ({(report.manual_blocks || []).length})
            </p>
            {(report.manual_blocks || []).length === 0 ? (
              <p className="text-sm text-[#a1a1aa]">
                No manual blocks with an amount in this range. Tip: append <code className="text-[#8cc63f]">· ₱X,XXX</code> to the private note when creating a block so it shows up here.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="text-[10px] uppercase tracking-widest text-[#a1a1aa]">
                    <tr>
                      <th className="text-left py-2">Date</th>
                      <th className="text-left py-2">Court</th>
                      <th className="text-left py-2">Slot</th>
                      <th className="text-left py-2">Note</th>
                      <th className="text-right py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.manual_blocks.map((m) => (
                      <tr key={m.id} className="border-t border-white/5">
                        <td className="py-2">{m.booking_date}</td>
                        <td className="py-2">{m.court_name}</td>
                        <td className="py-2 text-[#a1a1aa]">{m.time_slot}</td>
                        <td className="py-2 text-[#a1a1aa]">
                          {m.public_note || m.private_note}
                        </td>
                        <td className="py-2 text-right font-bold text-[#8cc63f]">
                          {money(m.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RangeReport;
